import { useCallback, useEffect, useState } from "react";

/**
 * Floating DAR (Action Orb) preferences — per device, stored locally so a guard
 * can hide or reposition the orb without an org-wide change.
 */
export type DarOrbPrefs = {
  enabled: boolean;
  side: "right" | "left";
  size: "regular" | "large";
  haptics: boolean;
};

const KEY = "missionready.dar.orb";

export const DEFAULT_ORB_PREFS: DarOrbPrefs = {
  enabled: true,
  side: "right",
  size: "regular",
  haptics: true,
};

function read(): DarOrbPrefs {
  if (typeof window === "undefined") return DEFAULT_ORB_PREFS;
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? { ...DEFAULT_ORB_PREFS, ...(JSON.parse(raw) as Partial<DarOrbPrefs>) } : DEFAULT_ORB_PREFS;
  } catch {
    return DEFAULT_ORB_PREFS;
  }
}

const listeners = new Set<(p: DarOrbPrefs) => void>();

export function useDarOrbPrefs() {
  const [prefs, setPrefs] = useState<DarOrbPrefs>(DEFAULT_ORB_PREFS);

  // Read after mount so SSR and hydration agree.
  useEffect(() => {
    setPrefs(read());
    const l = (p: DarOrbPrefs) => setPrefs(p);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);

  const update = useCallback((patch: Partial<DarOrbPrefs>) => {
    const next = { ...read(), ...patch };
    try {
      window.localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable — keep in-memory */
    }
    listeners.forEach((l) => l(next));
  }, []);

  return [prefs, update] as const;
}

export function buzz(ms = 12) {
  try {
    navigator.vibrate?.(ms);
  } catch {
    /* unsupported */
  }
}
